<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Security') }}
    </div>

    <div class="settings-content settings-columns-wrp">
      <div class="settings-column">
        <password-form />
        <second-factor-form />
        <latest-sessions-form
          :data-sessions="dataSessions"
          :reload-sessions="reloadSessions"
        />
      </div>
      <div class="settings-column">
        <api-keys-form :data-api-keys="dataApiKeys" :reload-api-keys="reloadApiKeys" />
        <landing-protection-form :secret-key="secretKey" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n, usePlanLimits } from '@/composables';
import { useStore } from '@/store';
import {
  getUserApiKeysApiRequest,
  getUserAccessTokensApiRequest,
  getUserLandingSecretKeyApiRequest,
} from '@/services/api/user-api';
import PasswordForm from './forms/password.vue';
import SecondFactorForm from './forms/second-factor.vue';
import ApiKeysForm from './forms/apikeys.vue';
import LatestSessionsForm from './forms/latest-sessions.vue';
import LandingProtectionForm from './forms/landing-protection.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { hasPlanLimitsRestriction } = usePlanLimits();

const dataApiKeys = ref<unknown | null>(null);
const dataSessions = ref<unknown | null>(null);
const secretKey = ref('');

const reloadApiKeys = async () => {
  dataApiKeys.value = await getUserApiKeysApiRequest();
};

const reloadSessions = async () => {
  dataSessions.value = await getUserAccessTokensApiRequest();
};

const load = async () => {
  const apiKeysPromise = getUserApiKeysApiRequest();
  const sessionsPromise = getUserAccessTokensApiRequest();
  const landingSecretPromise = !hasPlanLimitsRestriction('landingPageProtection')
    ? getUserLandingSecretKeyApiRequest()
    : Promise.resolve(null);
  const [apiKeysResult, sessionsResult, landingSecretResult] = await Promise.all([
    apiKeysPromise,
    sessionsPromise,
    landingSecretPromise,
  ]);
  dataApiKeys.value = apiKeysResult;
  dataSessions.value = sessionsResult;
  if (landingSecretResult) {
    secretKey.value = landingSecretResult.key;
  }
};

onMounted(() => {
  store.setLoading(true);
  load().finally(() => {
    store.setLoading(false);
  });
});
</script>

<style lang="scss" scoped></style>
